// <s-delivery-estimate> turns a business-day range into real dates in the
// shop's time zone, skipping weekends and rolling to the next day after the cutoff hour.
class DeliveryEstimate extends HTMLElement {
  connectedCallback() {
    const output = this.querySelector('[data-delivery-dates]');
    if (!output) return;

    const minDays = Number(this.dataset.minDays) || 0;
    const maxDays = Math.max(minDays, Number(this.dataset.maxDays) || minDays);
    const cutoffHour = Number(this.dataset.cutoffHour);
    const timeZone = this.dataset.timeZone || undefined;
    const locale = this.dataset.locale || document.documentElement.lang || undefined;

    try {
      const parts = Object.fromEntries(
        new Intl.DateTimeFormat('en-US', {
          timeZone,
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          hourCycle: 'h23',
        })
          .formatToParts(new Date())
          .map((part) => [part.type, part.value]),
      );

      // Work on a UTC date that represents "today" in the shop's time zone.
      let start = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)));
      const isWeekend = (date) => date.getUTCDay() === 0 || date.getUTCDay() === 6;
      const nextDay = (date) => new Date(date.getTime() + 86400000);

      // Orders after the cutoff, or on weekends, ship from the next business day.
      if (Number(parts.hour) >= cutoffHour || isWeekend(start)) {
        start = nextDay(start);
        while (isWeekend(start)) start = nextDay(start);
      }

      const addBusinessDays = (date, days) => {
        let result = date;
        let remaining = days;
        while (remaining > 0) {
          result = nextDay(result);
          if (!isWeekend(result)) remaining -= 1;
        }
        return result;
      };

      const earliest = addBusinessDays(start, minDays);
      const latest = addBusinessDays(start, maxDays);
      const format = new Intl.DateTimeFormat(locale, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC',
      });

      output.textContent =
        earliest.getTime() === latest.getTime()
          ? format.format(earliest)
          : `${format.format(earliest)} – ${format.format(latest)}`;
    } catch {
      // Keep the server-rendered "X–Y business days" fallback.
    }
  }
}

if (!customElements.get('s-delivery-estimate')) {
  customElements.define('s-delivery-estimate', DeliveryEstimate);
}
